import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { fuseAnimations } from '@fuse/animations';
import { Learnspace, InvitationType, LearnspaceUserRole } from '@shared/models';
import { User } from '@shared/models/user.model';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import { SendInvitationComponent } from '@shared/components/send-invitation/send-invitation.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { filter, switchMap } from 'rxjs/operators';

@Component({
  selector: 'program-participants',
  templateUrl: './program-participants.component.html',
  styleUrls: ['./program-participants.component.scss'],
  animations: fuseAnimations
})
export class ProgramParticipantsComponent implements OnChanges {
  @Input() learnspace: Learnspace;

  @ViewChild(MatTable, { static: false }) table: MatTable<any>;

  displayedColumns: string[] = ['name', 'email', 'role', 'actions'];
  isLoading = false;
  rows: User[] = [];
  readonly LearnspaceUserRole = LearnspaceUserRole;
  readonly roles = [
    { value: LearnspaceUserRole.contributor, label: 'Contributor' },
    { value: LearnspaceUserRole.learner, label: 'Learner' },
  ];

  constructor(
    private _courseApiService: CoursesAPIService,
    private _dialog: MatDialog
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['learnspace'] && this.learnspace) {
      this.loadData();
    }
  }

  loadData(): void {
    this.isLoading = true;
    this._courseApiService.getParticipants(this.learnspace.id).subscribe({
      next: (participants) => {
        this.rows = participants;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  getEmail(participant: User): string {
    return participant.email ?? '—';
  }

  getDisplayName(participant: User): string {
    return `${participant.first_name ?? ''} ${participant.last_name ?? ''}`.trim() || '—';
  }

  getRole(participant: User): string {
    const role = participant.lsRole;
    if (role === LearnspaceUserRole.contributor) return 'Contributor';
    if (role === LearnspaceUserRole.learner) return 'Learner';
    return '—';
  }

  changeRole(participant: User, role: LearnspaceUserRole): void {
    this._courseApiService.updateParticipantRole(this.learnspace.id, participant.id, role).subscribe({
      next: () => { participant.lsRole = role; },
    });
  }

  removeParticipant(participant: User): void {
    this._dialog
      .open(ConfirmationDialogComponent, {
        width: '400px',
        height: 'auto',
        panelClass: 'learnspace-theme',
        data: {
          title: 'Remove this participant',
          type: 'user',
          action: 'unsubscribe'
        }
      })
      .afterClosed()
      .pipe(
        filter((val) => val === 'ok'),
        switchMap(() => this._courseApiService.unsubscribeLearnspace(this.learnspace.id, participant.id))
      )
      .subscribe(() => {
        this.rows = this.rows.filter((u) => u.id !== participant.id);
        this.table.renderRows();
      });
  }

  openInviteDialog(defaultRole: LearnspaceUserRole): void {
    const dialogRef = this._dialog.open(SendInvitationComponent, {
      width: '48vw',
      height: '62vh',
      panelClass: ['learnspace-theme', 'invite-new-user-dialog']
    });
    dialogRef.componentInstance.showInvitationLink = true;
    dialogRef.componentInstance.targetId = this.learnspace.id;
    dialogRef.componentInstance.type = InvitationType.joinLearnspace;
    dialogRef.componentInstance.defaultRole = defaultRole;
    dialogRef.componentInstance.target = this.learnspace;
    dialogRef.componentInstance.feedback = true;
    dialogRef.componentInstance.instructionMessage = `Invite new ${defaultRole === LearnspaceUserRole.contributor ? 'contributors' : 'learners'} to this Program`;
    dialogRef.componentInstance.done.subscribe(() => {
      this._dialog.closeAll();
      this.loadData();
    });
  }
}
